import type { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AUTH_STRATEGY } from '@indutrust/auth-model';
import { resolveHttpError, withRequestStatus } from '@indutrust/shared-util';
import { mapResponse } from '@ngrx/operators';
import { signalStore } from '@ngrx/signals';
import {
  Events,
  on,
  withEventHandlers,
  withReducer,
} from '@ngrx/signals/events';
import { switchMap } from 'rxjs';
import { authSessionEvents } from '../auth-session';
import { loginEvents } from './login.events';

/**
 * Signal store for managing the login flow.
 *
 * Handles user login requests
 *
 * @example
 * readonly loginStore = inject(LoginStore);
 * readonly dispatch = injectDispatch(loginEvents);
 *
 * // In a component or guard
 * dispatch.loginRequested({ email: 'user@example.com', password: 'secret' });
 *
 * // Watch login state
 * effect(() => {
 *   if (loginStore.isSuccess() === 'success') {
 *     // Handle successful login
 *   }
 * });
 */
export const LoginStore = signalStore(
  withRequestStatus(),
  withReducer(
    on(loginEvents.loginRequested, () => ({ requestStatus: 'loading' })),
    on(loginEvents.loginSucceeded, () => ({
      requestStatus: 'success',
    })),
    on(loginEvents.loginFailed, ({ payload }) => ({
      requestStatus: { error: payload.error },
    })),
    on(
      authSessionEvents.userCompanyFailed,
      authSessionEvents.userDataFailed,
      authSessionEvents.logoutRequested,
      () => ({
        requestStatus: 'initial',
      })
    )
  ),
  withEventHandlers(
    (_, events = inject(Events), authService = inject(AUTH_STRATEGY)) => ({
      login$: events.on(loginEvents.loginRequested).pipe(
        switchMap(({ payload }) =>
          authService.login(payload.email, payload.password).pipe(
            mapResponse({
              next: (loginResponse) =>
                loginEvents.loginSucceeded({
                  accessToken: loginResponse.token,
                }),
              error: (error: HttpErrorResponse) =>
                loginEvents.loginFailed({
                  error: resolveHttpError(error, { loginAttempt: true }).code,
                }),
            })
          )
        )
      ),
    })
  )
);
